import {HexMath} from "../../HexMath";
import {coordsToKey} from "../../coords/coordsToKey";
import {keyToCoords} from "../../coords/keyToCoords";
import {COMMON_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {checkIfAnyQuarter} from "../../buildings/checkIfAnyQuarter";

// Токены, для которых ячейки дополнительно блокируются по потенциалу будущего
// квартала по соседству (см. calculateBlockedCellsForFutureQuarterPotential)
export const TOKENS_REQUIRING_FUTURE_QUARTER_POTENTIAL_CHECK = [
  COMMON_BUILDING_TOKENS.FARM,
  COMMON_BUILDING_TOKENS.BARRACKS,
  COMMON_BUILDING_TOKENS.QUARTER,
];

// Сообщения при клике на ячейку, заблокированную ЭТИМ правилом. Приоритет у
// исходной причины блокировки (calculateBlockedConstructionCells) — эти
// сообщения используются только для ячеек, которые НЕ заблокированы ей
// (см. calculateConstructionCells, per-cell resolution через Map)
export const FUTURE_QUARTER_POTENTIAL_ERROR_MESSAGES = {
  [COMMON_BUILDING_TOKENS.FARM]:
    'Здесь нельзя построить Ферму!\nТак как смежно ей не получится построить квартал.',
  [COMMON_BUILDING_TOKENS.BARRACKS]:
    'Здесь нельзя построить Казарму!\nТак как смежно ей не получится построить квартал.',
  [COMMON_BUILDING_TOKENS.QUARTER]:
    'Здесь нельзя построить Базовый квартал!\nТак как смежно ему не получится построить Центральный квартал.',
};

const hexMath = new HexMath(30, 'flat');

const getNeighborKeys = (cellKey) => {
  const [q, r] = keyToCoords(cellKey);
  return hexMath.getNeighborsFlatTop(q, r).map(([nq, nr]) => coordsToKey(nq, nr));
};

/**
 * Ячейки, смежные центральным кварталам ТЕКУЩЕГО игрока — единственное
 * исключение из зоны влияния кварталов для базового квартала (см.
 * calculateBlockedConstructionCellsForBaseQuarter): там его можно строить,
 * даже если ячейка сама попадает в чью-то зону влияния.
 */
const collectOwnCentralQuarterNeighbors = (citiesCache, currentPlayerColor) => {
  const neighbors = new Set();
  (citiesCache[currentPlayerColor] || []).forEach((city) => {
    if (!city.centralQuarter) {
      return;
    }
    getNeighborKeys(city.centralQuarter).forEach((neighborKey) => neighbors.add(neighborKey));
  });
  return neighbors;
};

/**
 * Возвращает подмножество availableCells, которое нужно ЗАБЛОКИРОВАТЬ (не
 * убрать из числа доступных, а подсветить красным — как и прочие причины
 * блокировки, см. calculateBlockedConstructionCells) для ферм/казарм/базовых
 * кварталов: тупиковые ячейки, рядом с которыми после строительства
 * невозможно заложить ни один квартал (город больше не сможет расшириться
 * через эту ячейку). Использует ту же зону влияния кварталов, что подсвечивает
 * игроку оверлей "Зона кварталов" (VisualToggles ->
 * HexGridRenderer.updateQuarterZoneOverlay, см. calculateQuarterZoneCells):
 * ячейка вне зоны годится под ЛЮБОЙ будущий квартал (в т.ч. новый город),
 * ячейка внутри зоны — только под базовый, и только если смежна центральному
 * кварталу текущего игрока.
 *
 * Для ферм/казарм проверяется соседство с ячейкой, допускающей ЛЮБОЙ будущий
 * квартал. Для самого базового квартала проверка мягче: кандидаты, УЖЕ смежные
 * своему центральному кварталу (обычное расширение города), не трогаем —
 * блокируются только "дальние" кандидаты, и для них требуется сосед именно под
 * НОВЫЙ центральный квартал (единственное осмысленное продолжение оттуда).
 *
 * Сосед, на котором УЖЕ стоит квартал (свой или чужой, центральный или
 * базовый), тоже засчитывается как допускающий "будущий" квартал — рост
 * города в эту сторону уже состоялся, дальше проверять нечего (иначе ЛЮБАЯ
 * ферма/казарма вплотную к центральному кварталу города — самое обычное
 * место застройки — ошибочно считалась бы тупиковой, если два ее остальных
 * соседа заняты, а прочие лежат вне соседнего кольца ЦК).
 *
 * @param selectedToken
 * @param {Array<string>} availableCells
 * @param {Object} hexes
 * @param citiesCache - учитывающий pendingBuildings кэш городов (futureCitiesCache)
 * @param currentPlayerColor
 * @param {Set<string>} quarterZoneCells
 * @returns {Set<string>} подмножество availableCells, которое нужно заблокировать
 */
export const calculateBlockedCellsForFutureQuarterPotential = (
  selectedToken, availableCells, hexes, citiesCache, currentPlayerColor, quarterZoneCells
) => {
  if (quarterZoneCells.size === 0) {
    return new Set(); // на поле еще нет ни одного квартала — блокировать нечего
  }

  const ownCentralQuarterNeighbors = collectOwnCentralQuarterNeighbors(citiesCache, currentPlayerColor);
  const isBaseQuarter = selectedToken === COMMON_BUILDING_TOKENS.QUARTER;

  // quarterZoneCells (см. calculateQuarterZoneCells) включает ТОЛЬКО пустые
  // ячейки и дороги — рудник/руины/гора/чужое строение в него не попадают
  // никогда, независимо от дистанции до квартала. Поэтому "нет в зоне" сам по
  // себе не значит "годится под квартал" — сначала нужно убедиться, что
  // ячейка вообще пуста или является дорогой (иначе почти любой кандидат
  // проходил бы фильтр через первый попавшийся смежный рудник/гору)
  const checkIfCellIsBuildable = (cellKey) => {
    const token = hexes[cellKey];
    return !token || token === TOKENS.ROAD;
  };

  const checkIfNeighborAllowsFutureQuarter = (neighborKey) => {
    // Сосед уже является кварталом (своим или чужим) — рост города в эту
    // сторону уже реализован, дальнейший потенциал проверять не нужно
    if (checkIfAnyQuarter(hexes[neighborKey])) {
      return true;
    }
    if (!checkIfCellIsBuildable(neighborKey)) {
      return false;
    }
    if (isBaseQuarter) {
      return !quarterZoneCells.has(neighborKey);
    }
    return !quarterZoneCells.has(neighborKey) || ownCentralQuarterNeighbors.has(neighborKey);
  };

  const blockedCells = new Set();
  availableCells.forEach((cellKey) => {
    if (isBaseQuarter && ownCentralQuarterNeighbors.has(cellKey)) {
      return; // штатное расширение города — не трогаем
    }
    const hasFutureQuarterNeighbor = getNeighborKeys(cellKey).some(checkIfNeighborAllowsFutureQuarter);
    if (!hasFutureQuarterNeighbor) {
      blockedCells.add(cellKey);
    }
  });

  return blockedCells;
};
